import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import { usePromotionSelection } from "../../../components/PromotionIQ/PromotionSelectionContext";
import {
  retailers,
  scenarioRules,
  calculateProjectedMetrics,
  formatCurrency,
  plannedPromotions,
  getRetailerName,
  getSkuInfo,
} from "../../../data/PromotionIQ/PromotionData";
import { Slider } from "../../../ui/slider";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChevronRight, Target, TrendingUp, Zap } from "lucide-react";
import React from "react";

const tools = ["PriceOnly", "Display", "Feature", "Instagram", "TikTok", "Facebook", "YouTube", "GoogleAds"] as const;

interface ScenarioConfig {
  name: string;
  discountPct: number;
  tools: string[];
  tradeSpend: number;
}

const Scenarios = () => {
  const navigate = useNavigate();
  const { selectedPromotionId } = usePromotionSelection();
  const sourcePromo = useMemo(
    () =>
      selectedPromotionId
        ? plannedPromotions.find((p) => p.promotion_id === selectedPromotionId)
        : undefined,
    [selectedPromotionId]
  );

  const [baseSpend, setBaseSpend] = useState(sourcePromo?.planned_trade_spend ?? 85000);
  const [selectedRetailer, setSelectedRetailer] = useState(sourcePromo?.retailer_id ?? "R1");

  // Re-sync state when navigating between promotions without remount.
  useEffect(() => {
    if (sourcePromo) {
      setBaseSpend(sourcePromo.planned_trade_spend);
      setSelectedRetailer(sourcePromo.retailer_id);
    }
  }, [sourcePromo]);

  const retailer = retailers.find((r) => r.retailer_id === selectedRetailer);
  const elasticity = retailer?.elasticity_sensitivity || "Medium";

  const builderTarget = "/promotioniq/promotion-builder";

  // Predefined scenarios for comparison (TRINITI Organics focused). When a specific
  // promotion is selected, prepend a "Current Plan" scenario built from its actual
  // configuration so the user can see how alternatives stack up against today's plan.
  const scenarios: ScenarioConfig[] = sourcePromo
    ? [
        {
          name: "Current Plan",
          discountPct: sourcePromo.planned_discount_pct,
          tools: sourcePromo.tools,
          tradeSpend: baseSpend,
        },
        { name: "Price Only", discountPct: 12, tools: ["PriceOnly"], tradeSpend: baseSpend },
        { name: "Display + Instagram", discountPct: 15, tools: ["Display", "Instagram"], tradeSpend: baseSpend },
        { name: "Full Social", discountPct: 18, tools: ["Display", "Instagram", "TikTok", "Facebook"], tradeSpend: baseSpend },
      ]
    : [
        { name: "Price Only", discountPct: 12, tools: ["PriceOnly"], tradeSpend: baseSpend },
        { name: "Display + Instagram", discountPct: 15, tools: ["Display", "Instagram"], tradeSpend: baseSpend },
        { name: "TikTok + YouTube", discountPct: 15, tools: ["TikTok", "YouTube"], tradeSpend: baseSpend },
        { name: "Full Social", discountPct: 18, tools: ["Display", "Instagram", "TikTok", "Facebook"], tradeSpend: baseSpend },
      ];

  const scenarioResults = scenarios.map((scenario) => {
    const metrics = calculateProjectedMetrics(
      scenario.tradeSpend,
      scenario.discountPct,
      scenario.tools,
      elasticity
    );
    return {
      name: scenario.name,
      ...metrics,
      spend: scenario.tradeSpend,
    };
  });

  const chartData = scenarioResults.map((s) => ({
    name: s.name,
    Revenue: s.projectedRevenue,
    Margin: s.projectedMargin,
  }));

  const bestScenario = scenarioResults.reduce((best, current) =>
    current.projectedMargin > best.projectedMargin ? current : best
  );

  const totalPotentialMargin = scenarioResults.reduce((sum, s) => sum + s.projectedMargin, 0) / scenarioResults.length;

  return (
    <div className="space-y-6">
      <ScreenHeader
        title={sourcePromo ? `Scenario Comparison · ${sourcePromo.promotion_id}` : "Scenario Comparison"}
        question={
          sourcePromo
            ? `Comparing alternatives for ${getRetailerName(sourcePromo.retailer_id)} · ${getSkuInfo(sourcePromo.sku_id)} (${sourcePromo.promo_window}).`
            : "Where should investment actually go?"
        }
      />

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <InteractiveCard
          label="Best Scenario"
          value={bestScenario.name}
          subValue={`${bestScenario.roi}x ROI`}
          variant="success"
          icon={TrendingUp}
          linkTo={builderTarget}
          linkLabel="Build it"
        />
        <InteractiveCard
          label="Best Margin"
          value={formatCurrency(bestScenario.projectedMargin)}
          subValue="Highest return"
          variant="highlight"
        />
        <InteractiveCard
          label="Avg Potential Margin"
          value={formatCurrency(totalPotentialMargin)}
          subValue="Across scenarios"
          variant="default"
        />
        <InteractiveCard
          label="Trade Spend"
          value={formatCurrency(baseSpend)}
          subValue="Per scenario"
          variant="default"
          linkTo="/promotioniq/promotion-portfolio"
          linkLabel="View portfolio"
        />
      </div>

      {/* Configuration */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Scenario Parameters</h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-athena">Retailer Context</label>
              <button 
                onClick={() => navigate(`/promotioniq/retailer-economics/${selectedRetailer}`)}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                View profile <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            <select
              value={selectedRetailer}
              onChange={(e) => setSelectedRetailer(e.target.value)}
              className="input-athena"
            >
              {retailers.map((r) => (
                <option key={r.retailer_id} value={r.retailer_id}>
                  {r.name} ({r.elasticity_sensitivity} elasticity)
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-athena">Base Trade Spend</label>
              <span className="text-xl font-bold text-primary">{formatCurrency(baseSpend)}</span>
            </div>
            <Slider
              value={[baseSpend]}
              onValueChange={(v) => setBaseSpend(v[0])}
              min={25000}
              max={150000}
              step={5000}
            />
          </div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Revenue vs Margin Comparison</h3>
          <button 
            onClick={() => navigate(builderTarget)}
            className="btn-athena text-sm"
          >
            Build Custom Scenario
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 20%)" />
              <XAxis dataKey="name" stroke="hsl(220, 10%, 55%)" fontSize={12} />
              <YAxis stroke="hsl(220, 10%, 55%)" fontSize={12} tickFormatter={(v) => `$${v / 1000}K`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(220, 18%, 10%)",
                  border: "1px solid hsl(220, 15%, 20%)",
                  borderRadius: "0",
                }}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Legend />
              <Bar dataKey="Revenue" fill="hsl(32, 95%, 55%)" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Margin" fill="hsl(142, 70%, 45%)" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {scenarioResults.map((scenario, idx) => (
          <div
            key={scenario.name}
            onClick={() => navigate(builderTarget)}
            className={`border p-6 transition-all cursor-pointer group ${
              scenario === bestScenario
                ? "border-success bg-success/5 hover:bg-success/10"
                : "border-border bg-card hover:border-primary hover:bg-primary/5"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">{scenario.name}</h4>
              {scenario === bestScenario ? (
                <span className="text-xs bg-success/20 text-success px-2 py-1">
                  Best
                </span>
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Revenue</span>
                <span className="text-primary font-medium">
                  {formatCurrency(scenario.projectedRevenue)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Margin</span>
                <span className={scenario.projectedMargin >= 0 ? "text-success" : "text-destructive"}>
                  {formatCurrency(scenario.projectedMargin)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ROI</span>
                <span className="font-medium">{scenario.roi}x</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground mb-1">Tools</p>
              <div className="flex flex-wrap gap-1">
                {scenarios[idx].tools.map((tool) => (
                  <span key={tool} className="text-xs bg-muted px-2 py-0.5">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tool Lift Reference */}
      <div 
        onClick={() => navigate("/promotioniq/shopper-influence")}
        className="border border-border bg-card p-6 cursor-pointer hover:border-primary transition-all group"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Tool Lift Reference</h3>
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Object.entries(scenarioRules.tool_lift).map(([tool, lift]) => (
            <div key={tool} className="text-center p-4 bg-muted/30 hover:bg-primary/10 transition-colors">
              <p className="text-sm text-muted-foreground">{tool}</p>
              <p className="text-2xl font-bold text-primary">{lift}x</p>
              <p className="text-xs text-muted-foreground">lift multiplier</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-primary mt-4 opacity-0 group-hover:opacity-100 transition-opacity text-center">
          Click to explore the full Shopper Influence Stack →
        </p>
      </div>

      {/* Related Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate(builderTarget)}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Build Custom Scenario</p>
                <p className="text-sm text-muted-foreground">Design your own investment</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/impact-panel")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Impact Panel</p>
                <p className="text-sm text-muted-foreground">Deep-dive on geo-fencing</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/negotiation")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-success" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Negotiation Prep</p>
                <p className="text-sm text-muted-foreground">Use scenarios in meetings</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This treats trade spend as a <span className="text-primary font-bold">portfolio of bets</span>, not entitlements. 
          Compare scenarios side-by-side to identify where investment should actually go.
        </p>
      </div>
    </div>
  );
};

export default Scenarios;
